import * as Yup from "yup";

import { UpdateReinsurerDeductions, UpdateReinsurerDeductionsVariables } from "_graphql/mutation/__generated__/UpdateReinsurerDeductions";
import { useMatch, useNavigate } from "react-location";

import { LocationGenerics } from "router/location";
import { Modal } from "components";
import { ModifyTreatyDeductionForm } from "components/forms";
import { TreatyType } from "_graphql/__generated__/globalTypes";
import _ from "lodash";
import numeral from "numeral";
import toast from "react-hot-toast";
import { updateTreatyReinsurersDeductions } from "_graphql/visal/mutations/treaty";
import { useAppSearch } from "_graphql/cache/auth";
import { useEffect } from "react";
import { useFormik } from "formik";
import { useMutation } from "@apollo/client";
import { useTreaty } from "hooks/data/use-treaties";
import { wrapClick } from "utils";

export const validationSchema = Yup.object().shape({
    requests: Yup.array()
        .of(
            Yup.object().shape({
                withholdingTax: Yup.number().min(0, "Number must be greater than 0")
                    .typeError("Should be a number")
                    .required("Withholding Tax is required"),
                nicLevy: Yup.number().min(0, "Number must be greater than 0")
                    .typeError("Should be a number")
                    .required("NIC Levy is required"),
                agreedBrokerage: Yup.number().min(0, "Number must be greater than 0")
                    .typeError("Should be a number")
                    .required("Agreed Brokerage Tax is required"),
                agreedCommission: Yup.number().min(0, "Number must be greater than 0")
                    .typeError("Should be a number")
                    .required("Agreed Commission is required"),
            })
        )
        .min(1)
        .required("At least one participant is required"),
});

export default function ModifyDeductionContainer({
    open,
    setOpen,
    refetch,
}: {
    open: boolean;
    setOpen: (val: boolean) => void;
    refetch?: () => void;
}) {
    const navigate = useNavigate<LocationGenerics>();
    const { params } = useMatch<LocationGenerics>();
    const searchParams = useAppSearch();
    const { treaty, loading: fetching } = useTreaty({
        treatyId: params.treaty || "",
    });

    const isProp = searchParams?.treaty_type === TreatyType.PROPORTIONAL;

    const [create, { loading }] = useMutation<
        UpdateReinsurerDeductions,
        UpdateReinsurerDeductionsVariables
    >(updateTreatyReinsurersDeductions, {
        refetchQueries: ["Treaty"],
    });


    const form = useFormik<any>({
        initialValues: {
            requests: [],
        },
        validationSchema: validationSchema,
        onSubmit: async (variables) => {
            const accountIds = treaty?.treaty_accounts?.map((item) => item?.treaty_account_id || "") || [];
            await create({
                variables: {
                    account_ids: accountIds,
                    data: variables.requests?.map((item: any) => ({
                        deductions: {
                            brokerage: numeral(item.agreedBrokerage).value() || 0,
                            commission: numeral(item.agreedCommission).value() || 0,
                            nic_levy: numeral(item.nicLevy).value() || 0,
                            withholding_tax: numeral(item.withholdingTax).value() || 0,
                            participating_percentage: numeral(item.treaty_participation_percentage).value() || 0,
                        },
                        participant_id: item.treaty_participation_id,
                        treaty_participant_deduction_id: item?.treaty_participant_deductions?.map((el: any) => el?.treaty_participant_deduction_id || "")
                    })),
                    isProp: searchParams?.treaty_type === "PROPORTIONAL",
                    layer: JSON.stringify(JSON.parse(treaty?.layer_limit || "[]")?.at(searchParams?.layer || 0) || {}),
                },
            }).then(({ data }) => {
                if (data?.updateReinsurerDeductions) {
                    toast.success("Deduction updated Successfully");
                    // navigate({ to: "/create-closing" });
                    refetch?.();
                    form.resetForm();
                } else {
                    toast.error("Deduction could not be updated");
                }
            });
        },
        onReset: () => {
            setOpen(false);
        },
    });

    useEffect(() => {
        if (treaty) {

            form.setFieldValue(
                "requests",
                isProp ? treaty?.treaty_participants?.map((item) => ({
                    ...item,
                    withholdingTax: _.first(item?.treaty_participant_deductions)?.withholding_tax || treaty?.treaty_deduction?.withholding_tax || 0,
                    nicLevy: _.first(item?.treaty_participant_deductions)?.nic_levy || treaty?.treaty_deduction?.nic_levy || 0,
                    agreedBrokerage: _.first(item?.treaty_participant_deductions)?.brokerage || treaty?.treaty_deduction?.brokerage || 0,
                    agreedCommission: _.first(item?.treaty_participant_deductions)?.commission || treaty?.treaty_deduction?.commission || 0,
                    participating_percentage: item?.treaty_participation_percentage || 0,
                    treaty_participant_deduction_id: _.first(item?.treaty_participant_deductions)?.treaty_participant_deduction_id || "",
                })) :
                    treaty?.treaty_participants?.filter(el => el?.layer_number === parseInt(searchParams?.layer || "1"))?.map((item) => ({
                        ...item,
                        withholdingTax: _.first(item?.treaty_participant_deductions)?.withholding_tax || treaty?.treaty_deduction?.withholding_tax || 0,
                        nicLevy: _.first(item?.treaty_participant_deductions)?.nic_levy || treaty?.treaty_deduction?.nic_levy || 0,
                        agreedBrokerage: _.first(item?.treaty_participant_deductions)?.brokerage || treaty?.treaty_deduction?.brokerage || 0,
                        agreedCommission: _.first(item?.treaty_participant_deductions)?.commission || treaty?.treaty_deduction?.commission || 0,
                        participating_percentage: item?.treaty_participation_percentage || 0,
                        treaty_participant_deduction_id: _.first(item?.treaty_participant_deductions)?.treaty_participant_deduction_id || "",
                    }))
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [treaty]);

    return (
        <Modal
            open={open}
            setOpen={setOpen}
            loading={fetching}
            title="Modify Deduction"
            size="7xl"
            description="Provide the percentage of the offer to be distributed to each reinsurer."
            renderActions={() => (
                <>
                    <button
                        type="button"
                        disabled={loading}
                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
                        onClick={wrapClick(form.handleSubmit)}
                    >
                        {loading ? "Modifying ..." : "Modify Deductions"}
                    </button>
                </>
            )}
        >
            {/* {JSON.stringify(form.values, null, 2)} */}
            <ModifyTreatyDeductionForm form={form} />
        </Modal>
    );
}
